import {
  differenceInCalendarDays,
  differenceInYears,
  format as formatDate,
} from 'date-fns';
import ruLocale from '@lt/components/utils/date/ruLocale';

import { DateTimeZoned } from '../DateTimeZoned';
import { CALENDAR_DATE_REGEX } from '../shared/constants';
import type { Brand } from '../shared/common';
import { createError } from '../shared/common';
import {
  formatCalendarDateParts,
  fromNativeDate,
  parseCalendarDateParts,
} from './lib';

export type CalendarDate = Brand<string, 'CalendarDate'>;

export const CalendarDate = {
  parse(value: string | Date): CalendarDate {
    if (value instanceof Date) {
      return this.fromNativeDate(value);
    }

    if (typeof value !== 'string') {
      throw createError(
        `Ожидалось значение, совместимое с CalendarDate, получено: ${String(
          value,
        )}`,
      );
    }

    if (value.includes('T')) {
      DateTimeZoned.parse(value);

      const [calendarDate] = value.split('T');
      parseCalendarDateParts(calendarDate);

      return calendarDate as CalendarDate;
    }

    if (!CALENDAR_DATE_REGEX.test(value)) {
      throw createError(
        `Ожидалась календарная дата в формате YYYY-MM-DD, получено: ${String(
          value,
        )}`,
      );
    }

    parseCalendarDateParts(value);
    return value as CalendarDate;
  },

  format(value: CalendarDate, pattern: string): string {
    return formatDate(this.toLocalDate(value), pattern, { locale: ruLocale });
  },

  /**
   * Преобразует календарную дату в `Date` с полуночью в UTC.
   * Удобно для сравнений и вычислений через `date-fns`, где локальная таймзона
   * пользователя не должна влиять на результат.
   */
  toNativeDate(value: CalendarDate): Date {
    return new Date(value);
  },

  /**
   * Преобразует календарную дату в `Date` с полуночью в локальной таймзоне.
   * Нужен для UI-контролов, которые интерпретируют `Date` как локальную дату,
   * например для datepicker-компонентов.
   */
  toLocalDate(value: CalendarDate): Date {
    const { year, month, day } = parseCalendarDateParts(value);
    return new Date(year, month - 1, day);
  },

  fromLocalDate(value: Date): CalendarDate {
    return formatCalendarDateParts({
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    }) as CalendarDate;
  },

  fromNativeDate(value: Date): CalendarDate {
    return formatCalendarDateParts(fromNativeDate(value)) as CalendarDate;
  },

  add(value: CalendarDate, amount: number): CalendarDate {
    const date = this.toNativeDate(value);
    date.setUTCDate(date.getUTCDate() + amount);
    return this.fromNativeDate(date);
  },

  sub(value: CalendarDate, amount: number): CalendarDate {
    return this.add(value, -amount);
  },

  difference(left: CalendarDate, right: CalendarDate): number {
    return differenceInCalendarDays(
      this.toNativeDate(left),
      this.toNativeDate(right),
    );
  },

  differenceInYears(left: CalendarDate, right: CalendarDate): number {
    return differenceInYears(this.toNativeDate(left), this.toNativeDate(right));
  },

  compare(left: CalendarDate, right: CalendarDate): number {
    if (left === right) return 0;
    return left > right ? 1 : -1;
  },
};
